import { useEffect, type ReactNode } from "react";
import { getConfig } from "../config";
import { useSessionStore } from "../store/sessionStore";
import { Button } from "../components/Button";
import { Spinner } from "../components/Spinner";
import { reportError } from "../services/eventBus";
import { useAuth } from "./AuthProvider";

interface AuthGuardProps {
  children: ReactNode;
}

interface FullPageMessageProps {
  title: string;
  message: string;
  action?: ReactNode;
}

function FullPageMessage({ title, message, action }: FullPageMessageProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
        <img src="/horizontal-optimyl-logo-black-text.svg" alt="Optimyl" className="mx-auto mb-6 h-8" />
        <h1 className="mb-2 text-2xl font-bold text-brand-navy">{title}</h1>
        <p className="mb-6 text-gray-600">{message}</p>
        {action}
      </div>
    </div>
  );
}

function FullPageSpinner({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3 text-gray-600">
        <Spinner />
        <p>{message}</p>
      </div>
    </div>
  );
}

/**
 * Gate for every /uw route. Skips sign-in when auth is disabled, otherwise shows the sign-in page until the
 * Identity Server session exists. Children render only once the workbench session (display name, role) has
 * loaded, so role-gated routes never judge the default role.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading: isAuthLoading, login } = useAuth();
  const user = useSessionStore((state) => state.user);
  const isLoading = useSessionStore((state) => state.isLoading);
  const loadError = useSessionStore((state) => state.loadError);
  const loadSession = useSessionStore((state) => state.loadSession);
  const authEnabled = getConfig().AUTH_ENABLED === true;
  const canEnter = !authEnabled || isAuthenticated;

  useEffect(() => {
    if (canEnter && !user && !isLoading && !loadError) void loadSession();
  }, [canEnter, user, isLoading, loadError, loadSession]);

  const handleSignIn = async () => {
    try {
      await login();
    } catch (error) {
      reportError(error, "AuthGuard.login");
    }
  };

  if (authEnabled && isAuthLoading) return <FullPageSpinner message="Signing in..." />;
  if (!canEnter) return <FullPageMessage title="UW Workbench" message="Sign in with your Optimyl account to continue." action={<Button onClick={() => void handleSignIn()}>Sign In</Button>} />;
  if (loadError && !user) return <FullPageMessage title="Unable to load your profile" message={loadError} action={<Button onClick={() => void loadSession()}>Retry</Button>} />;
  if (!user) return <FullPageSpinner message="Loading your profile..." />;

  return <>{children}</>;
}
